import { ValidationSchema } from 'forms';
import { StatutoryContributionsFormData } from './types';
import { portalStatus } from './data';
import {
  AADHAAR_REGEX,
  ESIC_IP_NUMBER_REGEX,
  NPS_PRAN_NUMBER_REGEX,
  PERSONAL_PAN_REGEX,
  PAN_REGEX,
} from 'utils/regexes';

export const validateStatutoryContributions: ValidationSchema<StatutoryContributionsFormData> = {
  pan: (formData) => {
    if (formData.isTdsEnabled) {
      if (!formData.pan) {
        return 'PAN is required';
      }
      if (!new RegExp(PERSONAL_PAN_REGEX).test(formData.pan)) {
        return 'Enter a valid PAN';
      }
    }
    return null;
  },

  aadharNumber: (formData) => {
    if (formData.aadharNumber) {
      if (!AADHAAR_REGEX.test(formData.aadharNumber)) {
        return 'Enter a valid 12-digit Aadhaar number';
      }
    }
    return null;
  },

  pfStatus: (formData) => {
    if (formData.isPfEnabled && (!formData.pfStatus || formData.pfStatus.length <= 0)) {
      return 'Please select PF status';
    }
    return null;
  },

  pfUan: (formData) => {
    const shouldValidatePfUan =
      formData.isPfEnabled && formData.pfStatus?.[0] === portalStatus.registered;
    if (shouldValidatePfUan && !formData.pfUan) {
      return 'UAN number is required for registered employees';
    }
    if (shouldValidatePfUan && formData.pfUan && !/^\d{12}$/.test(formData.pfUan)) {
      return 'Enter a valid 12-digit UAN number';
    }
    return null;
  },

  esicStatus: (formData) => {
    if (formData.isEsicEnabled && (!formData.esicStatus || formData.esicStatus.length <= 0)) {
      return 'Please select ESIC status';
    }
    return null;
  },

  esicIpNumber: (formData) => {
    const shouldValidateEsicIpNumber =
      formData.isEsicEnabled && formData.esicStatus?.[0] === portalStatus.registered;
    if (shouldValidateEsicIpNumber && !formData.esicIpNumber) {
      return 'ESIC IP number is required for registered employees';
    }
    if (
      shouldValidateEsicIpNumber &&
      formData.esicIpNumber &&
      !ESIC_IP_NUMBER_REGEX.test(formData.esicIpNumber)
    ) {
      return 'Enter a valid 10-digit ESIC IP number';
    }
    return null;
  },

  ptState: (formData) => {
    if (formData.isPtEnabled && (!formData.ptState || formData.ptState.length <= 0)) {
      return 'Please select PT state';
    }
    return null;
  },

  lwfLocation: (formData) => {
    if (formData.isLwfEnabled && (!formData.lwfLocation || formData.lwfLocation.length <= 0)) {
      return 'Please select LWF location';
    }
    return null;
  },

  npsPranNumber: (formData) => {
    if (formData.npsPranNumber && !NPS_PRAN_NUMBER_REGEX.test(formData.npsPranNumber)) {
      return 'Enter a valid 12-digit PRAN number';
    }
    return null;
  },
};

export const validateContractorStatutoryContributions = ({
  isContractorPanValidationEnabled,
}: {
  isContractorPanValidationEnabled: boolean;
}): ValidationSchema<StatutoryContributionsFormData> => ({
  pan: (formData) => {
    if (!formData.pan) {
      return 'PAN is required';
    }
    if (isContractorPanValidationEnabled && !PERSONAL_PAN_REGEX.test(formData.pan)) {
      return 'Enter a valid PAN';
    }

    if (!isContractorPanValidationEnabled && !new RegExp(PAN_REGEX).test(formData.pan)) {
      return 'Enter a valid PAN';
    }
    return null;
  },
  aadharNumber: (formData) => {
    if (isContractorPanValidationEnabled && !formData.aadharNumber) {
      return 'Aadhaar number is required';
    }
    if (formData.aadharNumber && !AADHAAR_REGEX.test(formData.aadharNumber)) {
      return 'Enter a valid 12-digit Aadhaar number';
    }
    return null;
  },
});

// The PF ceiling note is shown only for a registered employee whose PF wage is actually capped:
// the limit option, or the company default while the org-level PF limit is on
export const shouldShowPfCeilingNote = ({
  formData,
  isOrgPfLimitEnabled,
}: {
  formData: Partial<
    Pick<StatutoryContributionsFormData, 'isPfEnabled' | 'pfStatus' | 'pfWagesCalculationType'>
  >;
  isOrgPfLimitEnabled: boolean;
}) => {
  if (!formData.isPfEnabled || formData.pfStatus?.[0] !== portalStatus.registered) {
    return false;
  }
  const wageCalculationType = formData.pfWagesCalculationType?.[0];
  return wageCalculationType === '15k' || (wageCalculationType === 'default' && isOrgPfLimitEnabled);
};
